import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { fontStyles } from "components/Common/commonStyles";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SendIcon from "@mui/icons-material/Send";
import DraftsIcon from "@mui/icons-material/Drafts";

// ASTT-7319: shared by TrainingNotes.js and HandOffReport.js, replacing
// two separate, equal-weight buttons with one split-button -- "Send as
// Email" is the primary/default action, "Save as Draft" is a secondary
// action revealed via the dropdown arrow. Previously these screens styled
// Save as Draft with the bold FILLED button and Send Email with the
// lighter OUTLINED one -- backwards from the actual priority the story
// asks for. This component makes Send the visually prominent action.
const PrimaryActionButton = styled(Button)(() => ({
  ...fontStyles,
  fontSize: "12px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));

export default function SaveOrSendButton({
  onSendEmail,
  onSaveAsDraft,
  disabled = false,
  sendLabel = "Send as Email",
  saveLabel = "Save as Draft",
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef(null);

  const handleToggleMenu = () => {
    setMenuOpen((prevOpen) => !prevOpen);
  };

  const handleCloseMenu = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setMenuOpen(false);
  };

  const handleSaveAsDraftClick = (event) => {
    handleCloseMenu(event);
    onSaveAsDraft();
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <ButtonGroup
        variant="contained"
        ref={anchorRef}
        disabled={disabled}
        sx={{ boxShadow: "none" }}
      >
        <PrimaryActionButton
          onClick={onSendEmail}
          startIcon={<SendIcon />}
          sx={{ borderRadius: "8px 0 0 8px" }}
        >
          {sendLabel}
        </PrimaryActionButton>
        <PrimaryActionButton
          size="small"
          aria-controls={menuOpen ? "save-or-send-menu" : undefined}
          aria-expanded={menuOpen ? "true" : undefined}
          aria-label="More send options"
          aria-haspopup="menu"
          onClick={handleToggleMenu}
          sx={{ borderRadius: "0 8px 8px 0", paddingLeft: "4px", paddingRight: "4px" }}
        >
          <ArrowDropDownIcon />
        </PrimaryActionButton>
      </ButtonGroup>
      <Popper
        open={menuOpen}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        transition
        disablePortal
        sx={{ zIndex: 1300 }}
        // ASTT-7319 follow-up: the standard PopperJS "sameWidth" modifier
        // -- without it, this Popper sizes itself to "Save as Draft"'s
        // own natural content width, not the anchor (the full Send/arrow
        // button group), leaving them visibly mismatched. MUI's own
        // Autocomplete uses this identical pattern internally.
        modifiers={[
          {
            name: "sameWidth",
            enabled: true,
            phase: "beforeWrite",
            requires: ["computeStyles"],
            fn: ({ state }) => {
              state.styles.popper.width = `${state.rects.reference.width}px`;
            },
            effect: ({ state }) => {
              state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
            },
          },
        ]}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom-end" ? "right top" : "right bottom",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleCloseMenu}>
                <MenuList id="save-or-send-menu" autoFocusItem={menuOpen}>
                  <MenuItem onClick={handleSaveAsDraftClick}>
                    <DraftsIcon fontSize="small" sx={{ marginRight: 1 }} />
                    {saveLabel}
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
}

SaveOrSendButton.propTypes = {
  onSendEmail: PropTypes.func.isRequired,
  onSaveAsDraft: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  sendLabel: PropTypes.string,
  saveLabel: PropTypes.string,
};
